import { Component, OnInit, ViewChild, ElementRef, AfterContentInit, OnDestroy } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { forkJoin, Observable, of } from 'rxjs';
import { takeWhile, take, catchError } from 'rxjs/operators';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';

import { OnDestroyMixin, untilComponentDestroyed } from 'src/app/_shared/classes/component-destroy.class';
import * as fromStore from 'src/app/_store/_reducers';
import { FeaturesActions } from 'src/app/_store/_features/actions';
import { LayoutActions } from 'src/app/_store/_layout/actions';
import { DiligentApiService } from 'src/app/_shared/services';
import { NotificationsService } from 'src/app/_shared/modules/notifications/shared/notifications.service';
import { environment } from 'src/environments/environment';
import { EventTrackingService } from 'src/app/_shared/tracking/event-tracking.service';
import { IAccount } from 'src/app/_shared/classes/account';
type BannerConfig = {
  id: string;
  text: string;
  linkToActionEnabled: boolean;
  linkToActionText?: string;
  text2?: string;
  isClosable?: boolean;
};

type BannerListItem = BannerConfig & {
  hidden: boolean;
};

@Component({
  selector: 'app-head-banner',
  imports: [MatIcon, MatIconButton, TranslateModule, CommonModule],
  templateUrl: './head-banner.component.html',
  styleUrl: './head-banner.component.scss',
})
export class HeadBannerComponent extends OnDestroyMixin implements OnDestroy, OnInit, AfterContentInit {
  @ViewChild('bannerContainer', { static: false }) containerElement: ElementRef;
  // Display configuration for banners
  config: BannerConfig[] = [
    {
      id: 'showExpirationBanner',
      text: 'headBanners.subscriptionBanner',
      linkToActionEnabled: true,
      linkToActionText: 'headBanners.subscriptionBannerLink',
      text2: 'headBanners.subscriptionBanner2',
      isClosable: true,
    },
    {
      id: 'showPremiumTrialBanner',
      text: 'headBanners.premiumTrialBanner',
      linkToActionEnabled: false,
      isClosable: true,
    },
    {
      id: 'showPremiumPromoBanner2025',
      text: 'headBanners.premiumPromoBanner2025',
      linkToActionEnabled: true,
      linkToActionText: 'headBanners.premiumPromoBanner2025Link',
      text2: 'headBanners.premiumPromoBanner20252',
      isClosable: false,
    },
    {
      id: 'showNoSubscriptionUpgradeBanner',
      text: 'headBanners.noSubscriptionUpgradeBanner',
      linkToActionEnabled: true,
      linkToActionText: 'headBanners.noSubscriptionUpgradeBannerLink',
      text2: 'headBanners.noSubscriptionUpgradeBanner2',
      isClosable: false,
    },
    {
      id: 'tempErrorMsg',
      text: 'headBanners.tempErrorMsg',
      linkToActionEnabled: false,
      isClosable: true,
    },
  ];
  primaryAccExpiry$: Observable<string>;
  // List of banner to display
  bannersList: BannerListItem[] = [];
  bannerLoaded: boolean = false;

  trackingEnabled: boolean = environment.trackingEnabled;

  constructor(
    private store: Store<fromStore.State>,
    private diligentService: DiligentApiService,
    private notificationsService: NotificationsService,
    private translateService: TranslateService,
    private eventTrackingService: EventTrackingService,
    private router: Router
  ) {
    super();
  }
  ngOnInit(): void {
    this.store
      .select(fromStore.getFeatureBanners)
      .pipe(
        takeWhile(() => this.bannerLoaded === false),
        untilComponentDestroyed(this)
      )
      .subscribe((bannersFeatureFlags) => {
        // Create the banner list with only banner enabled in the store
        for (let banner of this.config) {
          if (bannersFeatureFlags?.[banner.id] === true) {
            this.bannersList.push({ ...banner, hidden: false });
          }
        }
        if (this.bannersList.length > 0) {
          this.bannerLoaded = true;
        }
        setTimeout(() => this.updateHeadBannerHeight(), 50);
      });

    this.primaryAccExpiry$ = this.store.select((state) => state.user.account.subscriptionExpiry);
  }

  ngAfterContentInit(): void {
    this.updateHeadBannerHeight();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  closeBanner(id: string): void {
    const banner = this.bannersList.find((item) => item.id === id);
    if (banner) {
      // Hide banner
      banner.hidden = true;
      // Dispatch action to hide banner till next login
      this.store.dispatch(FeaturesActions.setFeatureBannerVisibility({ payload: { [id]: false } }));
      // Emit banner element height
      setTimeout(() => this.updateHeadBannerHeight(), 50);
    }
  }

  onClickAction(bannerId: string): void {
    this.store
      .pipe(select(fromStore.getAccount))
      .pipe(take(1))
      .subscribe((userAccount: IAccount) => {
        if (bannerId == 'showPremiumPromoBanner2025') {
          // Send upgrade request
          return this.diligentService
            .requestToUpgradeSubscription(userAccount.id)
            .pipe(
              take(1),
              catchError((err) => of(err))
            )
            .subscribe((upgradeResponse) => {
              // Display response message to screen
              if (upgradeResponse.error) {
                this.notificationsService.alert(
                  upgradeResponse.error.message,
                  this.translateService.instant('headBanners.alertNotificationTitle')
                );
              } else {
                this.notificationsService.notify(this.translateService.instant('headBanners.upgradeRequestSent'));
              }
            });
        } else if (bannerId == 'showExpirationBanner' || bannerId == 'showNoSubscriptionUpgradeBanner') {
          if (this.trackingEnabled) {
            this.eventTrackingService.trackEvent(`banner.click`, {
              bannerId: bannerId,
              accountCreationDate: userAccount.creationDate,
              accountId: userAccount.id,
            });
          }

          this.router.navigate(['/manage-subscription']);
        }
      });
  }
  /**
   * Emit active banner count.
   * Used to communicate with parent and adjust layout
   */
  private updateHeadBannerHeight(): void {
    // Set section height in store
    if (this.containerElement) {
      this.store.dispatch(
        LayoutActions.setHeadBannerHeight({
          payload: { height: parseInt(this.containerElement?.nativeElement?.offsetHeight) },
        })
      );
    }
  }
}
